import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import { message } from 'antd';
import { AppDispatch, resetReduxState } from '../store';
import { signOut } from '../services/authService';
import { broadcastLogout } from '../utils/authEventBroadcast';

const IDLE_TIMEOUT = 1000 * 60 * 30; // 30 minutes
const PROMPT_BEFORE_IDLE = 1000 * 60 * 5; // 5 minutes before idle (shows at 25 min)
const COUNTDOWN_INTERVAL = 1000; // Update countdown every second

interface UseIdleLogoutOptions {
  disabled?: boolean;
}

interface UseIdleLogoutReturn {
  showWarning: boolean;
  remainingTime: number;
  handleStayLoggedIn: () => void;
  handleLogoutNow: () => void;
}

export function useIdleLogout({ disabled = false }: UseIdleLogoutOptions = {}): UseIdleLogoutReturn {
  const dispatch = useDispatch<AppDispatch>();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(PROMPT_BEFORE_IDLE);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLoggingOutRef = useRef(false);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const performLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    clearCountdown();
    setShowWarning(false);

    try {
      await signOut();
    } catch {
      // Proceed with client-side cleanup even if server call fails
    }

    broadcastLogout('inactivity');
    dispatch(resetReduxState());
    message.info('You have been logged out due to inactivity.');
    window.location.href = '/auth/signin';
  }, [clearCountdown, dispatch]);

  const { getRemainingTime, activate } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: PROMPT_BEFORE_IDLE,
    crossTab: true,
    syncTimers: 200,
    disabled,

    onPrompt: () => {
      setShowWarning(true);
      setRemainingTime(Math.ceil(getRemainingTime() / 1000) * 1000);

      countdownRef.current = setInterval(() => {
        const remaining = getRemainingTime();
        setRemainingTime(remaining);

        if (remaining <= 0) {
          clearCountdown();
        }
      }, COUNTDOWN_INTERVAL);
    },

    onIdle: () => {
      performLogout();
    },

    onActive: () => {
      clearCountdown();
      setShowWarning(false);
      setRemainingTime(PROMPT_BEFORE_IDLE);
    },
  });

  useEffect(() => {
    return () => {
      clearCountdown();
    };
  }, [clearCountdown]);

  const handleStayLoggedIn = useCallback(() => {
    clearCountdown();
    setShowWarning(false);
    setRemainingTime(PROMPT_BEFORE_IDLE);
    activate();
  }, [clearCountdown, activate]);

  const handleLogoutNow = useCallback(() => {
    performLogout();
  }, [performLogout]);

  return {
    showWarning,
    remainingTime,
    handleStayLoggedIn,
    handleLogoutNow,
  };
}
